/**
 * Fire & Life Safety Renderer
 * Draws NFPA 170-style vector symbols on the canvas.
 *
 * Each item: { id, shape, x, y, rotation, size (inches), color, code, arrow, label }
 */

import { GRID_SIZE } from '../../../constants/grid';

const SELECTION_COLOR = '#00c8ff';
const LABEL_COLOR = '#1a1a1a';
const PX_PER_INCH = GRID_SIZE / 6; // matches furnitureRenderer convention (6" per grid cell)

export function drawFireSafety(ctx, item, isSelected = false, scale = 1) {
  const { x, y, rotation = 0, size = 12, color = '#d62828', shape } = item;
  const px = size * PX_PER_INCH;

  ctx.save();
  ctx.translate(x, y);
  ctx.rotate((rotation * Math.PI) / 180);

  const stroke = isSelected ? SELECTION_COLOR : color;
  const lineW = Math.max(1, 1.5 / scale);

  ctx.lineWidth = lineW;
  ctx.strokeStyle = stroke;
  ctx.fillStyle = color;

  switch (shape) {
    case 'extinguisher':         drawExtinguisher(ctx, px, color, stroke, item.code); break;
    case 'extinguisher-cabinet': drawExtinguisherCabinet(ctx, px, color, stroke, item.code); break;
    case 'fire-blanket':         drawFireBlanket(ctx, px, color, stroke, item.code); break;
    case 'hose-cabinet':         drawHoseCabinet(ctx, px, color, stroke, item.code); break;
    case 'standpipe':            drawStandpipe(ctx, px, color, stroke, item.code); break;
    case 'fdc':                  drawFDC(ctx, px, color, stroke, item.code); break;

    case 'sprinkler-pendant':    drawSprinklerPendant(ctx, px, color, stroke); break;
    case 'sprinkler-upright':    drawSprinklerUpright(ctx, px, color, stroke); break;
    case 'sprinkler-sidewall':   drawSprinklerSidewall(ctx, px, color, stroke); break;
    case 'sprinkler-concealed':  drawSprinklerConcealed(ctx, px, color, stroke); break;

    case 'detector':             drawDetector(ctx, px, color, stroke, item.code); break;
    case 'duct-detector':        drawDuctDetector(ctx, px, color, stroke, item.code); break;
    case 'beam-detector':        drawBeamDetector(ctx, px, color, stroke, item.code); break;

    case 'pull-station':         drawPullStation(ctx, px, color, stroke, item.code); break;
    case 'horn-strobe':          drawHornStrobe(ctx, px, color, stroke, item.code); break;
    case 'strobe':               drawStrobe(ctx, px, color, stroke, item.code); break;
    case 'horn':                 drawHorn(ctx, px, color, stroke, item.code); break;
    case 'speaker':              drawSpeaker(ctx, px, color, stroke, item.code); break;
    case 'control-panel':        drawControlPanel(ctx, px, color, stroke, item.code); break;

    case 'exit-sign':            drawExitSign(ctx, px, color, stroke); break;
    case 'exit-sign-arrow':      drawExitSignArrow(ctx, px, color, stroke, item.arrow); break;
    case 'emergency-light':      drawEmergencyLight(ctx, px, color, stroke, item.code); break;
    case 'combo-exit-em':        drawComboExitEM(ctx, px, color, stroke); break;
    case 'egress-arrow':         drawEgressArrow(ctx, px, color, stroke); break;
    case 'assembly-point':       drawAssemblyPoint(ctx, px, color, stroke, item.code); break;
    case 'fire-door':            drawFireDoor(ctx, px, color, stroke, item.code); break;

    case 'aed':                  drawAED(ctx, px, color, stroke, item.code); break;
    case 'first-aid':            drawFirstAid(ctx, px, color, stroke); break;
    case 'eyewash':              drawEyewash(ctx, px, color, stroke, item.code); break;
    case 'safety-shower':        drawSafetyShower(ctx, px, color, stroke, item.code); break;
    case 'knox-box':             drawKnoxBox(ctx, px, color, stroke, item.code); break;

    default:                     drawFallback(ctx, px, color, stroke, item.code || '?');
  }

  // Selection halo (un-rotated so it's always rectangular)
  if (isSelected) {
    ctx.rotate((-rotation * Math.PI) / 180);
    const halo = px * 0.75;
    ctx.strokeStyle = SELECTION_COLOR;
    ctx.lineWidth = Math.max(1, 1 / scale);
    ctx.setLineDash([4 / scale, 3 / scale]);
    ctx.strokeRect(-halo, -halo, halo * 2, halo * 2);
    ctx.setLineDash([]);
  }

  ctx.restore();
}

// ============ helpers ============

function fillCircle(ctx, r, fill, stroke) {
  ctx.beginPath();
  ctx.arc(0, 0, r, 0, Math.PI * 2);
  if (fill) { ctx.fillStyle = fill; ctx.fill(); }
  ctx.strokeStyle = stroke;
  ctx.stroke();
}

function drawCodeLabel(ctx, text, fontPx, color = '#ffffff') {
  if (!text) return;
  ctx.fillStyle = color;
  ctx.font = `bold ${fontPx}px Arial, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, 0, 0);
}

// ============ Fire Suppression ============

function drawExtinguisher(ctx, px, color, stroke, code) {
  const r = px / 2;
  fillCircle(ctx, r, color, stroke);
  // Upward triangle (NFPA)
  ctx.beginPath();
  ctx.moveTo(0, -r * 0.55);
  ctx.lineTo(r * 0.5, r * 0.35);
  ctx.lineTo(-r * 0.5, r * 0.35);
  ctx.closePath();
  ctx.fillStyle = '#ffffff';
  ctx.fill();
  ctx.strokeStyle = '#ffffff';
  ctx.stroke();
  drawCodeLabel(ctx, code, r * 0.5, color);
}

function drawExtinguisherCabinet(ctx, px, color, stroke, code) {
  const half = px / 2;
  ctx.fillStyle = color;
  ctx.fillRect(-half, -half, px, px);
  ctx.strokeStyle = stroke;
  ctx.strokeRect(-half, -half, px, px);
  // Inner extinguisher glyph
  ctx.save();
  ctx.scale(0.55, 0.55);
  drawExtinguisher(ctx, px, '#ffffff', color, '');
  ctx.restore();
  drawCodeLabel(ctx, code, half * 0.45, '#ffffff');
}

function drawFireBlanket(ctx, px, color, stroke, code) {
  const half = px / 2;
  ctx.fillStyle = color;
  ctx.fillRect(-half, -half, px, px);
  ctx.strokeStyle = stroke;
  ctx.strokeRect(-half, -half, px, px);
  drawCodeLabel(ctx, code || 'FB', half * 0.7, '#ffffff');
}

function drawHoseCabinet(ctx, px, color, stroke, code) {
  const half = px / 2;
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(-half, -half, px, px);
  ctx.strokeStyle = color;
  ctx.lineWidth *= 1.5;
  ctx.strokeRect(-half, -half, px, px);
  // Hose coil
  ctx.beginPath();
  ctx.arc(0, 0, half * 0.4, 0, Math.PI * 2);
  ctx.strokeStyle = color;
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(0, 0, half * 0.2, 0, Math.PI * 2);
  ctx.stroke();
  drawCodeLabel(ctx, code, half * 0.35, color);
}

function drawStandpipe(ctx, px, color, stroke, code) {
  const r = px / 2;
  fillCircle(ctx, r, '#ffffff', color);
  drawCodeLabel(ctx, code || 'SP', r * 0.7, color);
}

function drawFDC(ctx, px, color, stroke, code) {
  // Triangle pointing up, filled red (NFPA convention)
  const r = px / 2;
  ctx.beginPath();
  ctx.moveTo(0, -r);
  ctx.lineTo(r * 0.95, r * 0.7);
  ctx.lineTo(-r * 0.95, r * 0.7);
  ctx.closePath();
  ctx.fillStyle = color;
  ctx.fill();
  ctx.strokeStyle = stroke;
  ctx.stroke();
  ctx.fillStyle = '#ffffff';
  ctx.font = `bold ${r * 0.45}px Arial, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(code || 'FDC', 0, r * 0.2);
}

// ============ Sprinklers ============

function drawSprinklerPendant(ctx, px, color, stroke) {
  const r = px / 2;
  fillCircle(ctx, r, '#ffffff', color);
  // X inside (deflector pattern)
  ctx.beginPath();
  ctx.moveTo(-r * 0.6, -r * 0.6);
  ctx.lineTo(r * 0.6, r * 0.6);
  ctx.moveTo(r * 0.6, -r * 0.6);
  ctx.lineTo(-r * 0.6, r * 0.6);
  ctx.strokeStyle = color;
  ctx.stroke();
}

function drawSprinklerUpright(ctx, px, color, stroke) {
  const r = px / 2;
  fillCircle(ctx, r, '#ffffff', color);
  // Plus inside
  ctx.beginPath();
  ctx.moveTo(0, -r * 0.7);
  ctx.lineTo(0, r * 0.7);
  ctx.moveTo(-r * 0.7, 0);
  ctx.lineTo(r * 0.7, 0);
  ctx.strokeStyle = color;
  ctx.stroke();
}

function drawSprinklerSidewall(ctx, px, color, stroke) {
  const r = px / 2;
  // Half circle facing right
  ctx.beginPath();
  ctx.arc(0, 0, r, -Math.PI / 2, Math.PI / 2);
  ctx.lineTo(0, -r);
  ctx.closePath();
  ctx.fillStyle = '#ffffff';
  ctx.fill();
  ctx.strokeStyle = color;
  ctx.stroke();
  // Arrow indicating throw direction
  ctx.beginPath();
  ctx.moveTo(r * 0.2, 0);
  ctx.lineTo(r * 0.7, 0);
  ctx.moveTo(r * 0.5, -r * 0.2);
  ctx.lineTo(r * 0.7, 0);
  ctx.lineTo(r * 0.5, r * 0.2);
  ctx.stroke();
}

function drawSprinklerConcealed(ctx, px, color, stroke) {
  const r = px / 2;
  fillCircle(ctx, r, '#ffffff', color);
  fillCircle(ctx, r * 0.55, color, color);
}

// ============ Detection ============

function drawDetector(ctx, px, color, stroke, code) {
  const r = px / 2;
  fillCircle(ctx, r, '#ffffff', color);
  drawCodeLabel(ctx, code, r * 0.85, color);
}

function drawDuctDetector(ctx, px, color, stroke, code) {
  const half = px / 2;
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(-half, -half * 0.65, px, px * 0.65);
  ctx.strokeStyle = color;
  ctx.strokeRect(-half, -half * 0.65, px, px * 0.65);
  // Tick marks (duct flow indicators)
  ctx.beginPath();
  ctx.moveTo(-half * 0.7, -half * 0.65);
  ctx.lineTo(-half * 0.7, half * 0.65);
  ctx.moveTo(half * 0.7, -half * 0.65);
  ctx.lineTo(half * 0.7, half * 0.65);
  ctx.stroke();
  drawCodeLabel(ctx, code, half * 0.4, color);
}

function drawBeamDetector(ctx, px, color, stroke, code) {
  const r = px / 2;
  fillCircle(ctx, r, '#ffffff', color);
  // Beam line through it
  ctx.beginPath();
  ctx.moveTo(-r, 0);
  ctx.lineTo(r, 0);
  ctx.strokeStyle = color;
  ctx.stroke();
  drawCodeLabel(ctx, code, r * 0.5, color);
}

// ============ Notification ============

function drawPullStation(ctx, px, color, stroke, code) {
  const half = px / 2;
  ctx.fillStyle = color;
  ctx.fillRect(-half, -half, px, px);
  ctx.strokeStyle = stroke;
  ctx.strokeRect(-half, -half, px, px);
  drawCodeLabel(ctx, code || 'F', half * 1.2, '#ffffff');
}

function drawHornStrobe(ctx, px, color, stroke, code) {
  const half = px / 2;
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(-half, -half, px, px);
  ctx.strokeStyle = color;
  ctx.strokeRect(-half, -half, px, px);
  // Strobe rays from corner
  ctx.beginPath();
  ctx.moveTo(-half * 0.6, -half * 0.6);
  ctx.lineTo(-half, -half);
  ctx.moveTo(-half * 0.4, -half * 0.8);
  ctx.lineTo(-half * 0.2, -half);
  ctx.moveTo(-half * 0.8, -half * 0.4);
  ctx.lineTo(-half, -half * 0.2);
  ctx.stroke();
  drawCodeLabel(ctx, code, half * 0.6, color);
}

function drawStrobe(ctx, px, color, stroke, code) {
  // Triangle with V inside
  const r = px / 2;
  ctx.beginPath();
  ctx.moveTo(0, -r);
  ctx.lineTo(r * 0.95, r * 0.7);
  ctx.lineTo(-r * 0.95, r * 0.7);
  ctx.closePath();
  ctx.fillStyle = '#ffffff';
  ctx.fill();
  ctx.strokeStyle = color;
  ctx.stroke();
  ctx.fillStyle = color;
  ctx.font = `bold ${r * 0.6}px Arial, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(code || 'V', 0, r * 0.25);
}

function drawHorn(ctx, px, color, stroke, code) {
  const half = px / 2;
  // Horn shape (trapezoid opening right)
  ctx.beginPath();
  ctx.moveTo(-half * 0.7, -half * 0.5);
  ctx.lineTo(half * 0.3, -half * 0.9);
  ctx.lineTo(half * 0.3, half * 0.9);
  ctx.lineTo(-half * 0.7, half * 0.5);
  ctx.closePath();
  ctx.fillStyle = '#ffffff';
  ctx.fill();
  ctx.strokeStyle = color;
  ctx.stroke();
  // Sound waves
  ctx.beginPath();
  ctx.arc(half * 0.5, 0, half * 0.3, -Math.PI / 4, Math.PI / 4);
  ctx.arc(half * 0.5, 0, half * 0.55, -Math.PI / 4, Math.PI / 4);
  ctx.stroke();
}

function drawSpeaker(ctx, px, color, stroke, code) {
  const r = px / 2;
  fillCircle(ctx, r, '#ffffff', color);
  // Inner concentric circle for speaker cone
  fillCircle(ctx, r * 0.55, '#ffffff', color);
  drawCodeLabel(ctx, code, r * 0.35, color);
}

function drawControlPanel(ctx, px, color, stroke, code) {
  const half = px / 2;
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(-half, -half, px, px);
  ctx.strokeStyle = color;
  ctx.lineWidth *= 1.5;
  ctx.strokeRect(-half, -half, px, px);
  // Mini screen
  ctx.fillStyle = color;
  ctx.fillRect(-half * 0.7, -half * 0.7, px * 0.7, px * 0.35);
  drawCodeLabel(ctx, code, half * 0.45, color);
}

// ============ Egress ============

function drawExitSign(ctx, px, color, stroke) {
  const halfW = px * 0.9;
  const halfH = px * 0.45;
  ctx.fillStyle = color;
  ctx.fillRect(-halfW, -halfH, halfW * 2, halfH * 2);
  ctx.strokeStyle = stroke;
  ctx.strokeRect(-halfW, -halfH, halfW * 2, halfH * 2);
  ctx.fillStyle = '#ffffff';
  ctx.font = `bold ${halfH * 1.0}px Arial, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('EXIT', 0, 0);
}

function drawExitSignArrow(ctx, px, color, stroke, arrow) {
  const halfW = px * 1.1;
  const halfH = px * 0.45;
  ctx.fillStyle = color;
  ctx.fillRect(-halfW, -halfH, halfW * 2, halfH * 2);
  ctx.strokeStyle = stroke;
  ctx.strokeRect(-halfW, -halfH, halfW * 2, halfH * 2);

  ctx.fillStyle = '#ffffff';
  ctx.font = `bold ${halfH * 0.95}px Arial, sans-serif`;
  ctx.textBaseline = 'middle';

  const dir = arrow === 'left' ? -1 : 1;
  if (dir < 0) {
    ctx.textAlign = 'left';
    ctx.fillText('EXIT', -halfW * 0.4, 0);
    drawArrow(ctx, -halfW * 0.65, 0, -halfW * 0.9, 0, halfH * 0.6, '#ffffff');
  } else {
    ctx.textAlign = 'right';
    ctx.fillText('EXIT', halfW * 0.4, 0);
    drawArrow(ctx, halfW * 0.65, 0, halfW * 0.9, 0, halfH * 0.6, '#ffffff');
  }
}

function drawEmergencyLight(ctx, px, color, stroke, code) {
  const halfW = px * 0.9;
  const halfH = px * 0.5;
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(-halfW, -halfH, halfW * 2, halfH * 2);
  ctx.strokeStyle = color;
  ctx.strokeRect(-halfW, -halfH, halfW * 2, halfH * 2);
  // Two lamp heads
  fillCircle2(ctx, -halfW * 0.45, halfH * 0.1, halfH * 0.45, color);
  fillCircle2(ctx, halfW * 0.45, halfH * 0.1, halfH * 0.45, color);
  ctx.fillStyle = color;
  ctx.font = `bold ${halfH * 0.7}px Arial, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(code || 'EM', 0, -halfH * 0.5);
}

function drawComboExitEM(ctx, px, color, stroke) {
  // Wider exit sign with two bumps for EM lights underneath
  const halfW = px * 1.0;
  const halfH = px * 0.55;
  ctx.fillStyle = color;
  ctx.fillRect(-halfW, -halfH, halfW * 2, halfH * 1.2);
  ctx.strokeStyle = stroke;
  ctx.strokeRect(-halfW, -halfH, halfW * 2, halfH * 2);
  ctx.fillStyle = '#ffffff';
  ctx.font = `bold ${halfH * 0.6}px Arial, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('EXIT', 0, -halfH * 0.35);
  fillCircle2(ctx, -halfW * 0.45, halfH * 0.55, halfH * 0.3, '#ffffff');
  fillCircle2(ctx, halfW * 0.45, halfH * 0.55, halfH * 0.3, '#ffffff');
}

function drawEgressArrow(ctx, px, color, stroke) {
  // Bold arrow pointing right
  const half = px / 2;
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.moveTo(-half, -half * 0.3);
  ctx.lineTo(half * 0.3, -half * 0.3);
  ctx.lineTo(half * 0.3, -half * 0.6);
  ctx.lineTo(half, 0);
  ctx.lineTo(half * 0.3, half * 0.6);
  ctx.lineTo(half * 0.3, half * 0.3);
  ctx.lineTo(-half, half * 0.3);
  ctx.closePath();
  ctx.fill();
  ctx.strokeStyle = stroke;
  ctx.stroke();
}

function drawAssemblyPoint(ctx, px, color, stroke, code) {
  const r = px / 2;
  fillCircle(ctx, r, color, stroke);
  // Four inward arrows
  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth *= 1.5;
  for (let i = 0; i < 4; i++) {
    const angle = (i * Math.PI) / 2;
    const ox = Math.cos(angle) * r * 0.85;
    const oy = Math.sin(angle) * r * 0.85;
    const ix = Math.cos(angle) * r * 0.25;
    const iy = Math.sin(angle) * r * 0.25;
    ctx.beginPath();
    ctx.moveTo(ox, oy);
    ctx.lineTo(ix, iy);
    // Arrowhead
    const perpA = angle + Math.PI - 0.4;
    const perpB = angle + Math.PI + 0.4;
    ctx.lineTo(ix + Math.cos(perpA) * r * 0.15, iy + Math.sin(perpA) * r * 0.15);
    ctx.moveTo(ix, iy);
    ctx.lineTo(ix + Math.cos(perpB) * r * 0.15, iy + Math.sin(perpB) * r * 0.15);
    ctx.stroke();
  }
}

function drawFireDoor(ctx, px, color, stroke, code) {
  const half = px / 2;
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(-half, -half, px, px);
  ctx.strokeStyle = color;
  ctx.lineWidth *= 1.5;
  ctx.strokeRect(-half, -half, px, px);
  drawCodeLabel(ctx, code || 'FD', half * 0.9, color);
}

// ============ Medical ============

function drawAED(ctx, px, color, stroke, code) {
  const half = px / 2;
  ctx.fillStyle = color;
  ctx.fillRect(-half, -half, px, px);
  ctx.strokeStyle = stroke;
  ctx.strokeRect(-half, -half, px, px);
  // Heart
  ctx.fillStyle = '#ffffff';
  ctx.beginPath();
  const hr = half * 0.35;
  ctx.moveTo(0, -hr * 0.2);
  ctx.bezierCurveTo(-hr, -hr * 1.4, -hr * 1.6, hr * 0.2, 0, hr * 1.0);
  ctx.bezierCurveTo(hr * 1.6, hr * 0.2, hr, -hr * 1.4, 0, -hr * 0.2);
  ctx.fill();
  drawCodeLabel(ctx, code, half * 0.4, '#ffffff');
}

function drawFirstAid(ctx, px, color, stroke) {
  const half = px / 2;
  ctx.fillStyle = color;
  ctx.fillRect(-half, -half, px, px);
  ctx.strokeStyle = stroke;
  ctx.strokeRect(-half, -half, px, px);
  // White cross
  ctx.fillStyle = '#ffffff';
  const arm = half * 0.7;
  const thick = half * 0.25;
  ctx.fillRect(-thick, -arm, thick * 2, arm * 2);
  ctx.fillRect(-arm, -thick, arm * 2, thick * 2);
}

function drawEyewash(ctx, px, color, stroke, code) {
  const r = px / 2;
  fillCircle(ctx, r, color, stroke);
  // Eye shape (lens) inside, white
  ctx.fillStyle = '#ffffff';
  ctx.beginPath();
  ctx.ellipse(0, 0, r * 0.65, r * 0.4, 0, 0, Math.PI * 2);
  ctx.fill();
  // Pupil
  ctx.fillStyle = color;
  fillCircle2(ctx, 0, 0, r * 0.22, color);
}

function drawSafetyShower(ctx, px, color, stroke, code) {
  const half = px / 2;
  ctx.fillStyle = color;
  ctx.fillRect(-half, -half, px, px);
  ctx.strokeStyle = stroke;
  ctx.strokeRect(-half, -half, px, px);
  // Shower head
  ctx.fillStyle = '#ffffff';
  ctx.beginPath();
  ctx.arc(0, -half * 0.3, half * 0.35, 0, Math.PI);
  ctx.closePath();
  ctx.fill();
  // Water drops
  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth *= 1.5;
  for (let i = -1; i <= 1; i++) {
    ctx.beginPath();
    ctx.moveTo(i * half * 0.25, half * 0.05);
    ctx.lineTo(i * half * 0.25, half * 0.6);
    ctx.stroke();
  }
}

function drawKnoxBox(ctx, px, color, stroke, code) {
  const half = px / 2;
  ctx.fillStyle = color;
  ctx.fillRect(-half, -half, px, px);
  ctx.strokeStyle = stroke;
  ctx.strokeRect(-half, -half, px, px);
  drawCodeLabel(ctx, code || 'KX', half * 1.1, '#ffffff');
}

// ============ Fallback ============

function drawFallback(ctx, px, color, stroke, code) {
  const r = px / 2;
  fillCircle(ctx, r, '#ffffff', color);
  drawCodeLabel(ctx, code, r * 0.8, color);
}

// ============ tiny helpers ============

function fillCircle2(ctx, cx, cy, r, fill) {
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.fillStyle = fill;
  ctx.fill();
}

function drawArrow(ctx, fromX, fromY, toX, toY, headSize, color) {
  ctx.strokeStyle = color;
  ctx.fillStyle = color;
  const angle = Math.atan2(toY - fromY, toX - fromX);
  ctx.beginPath();
  ctx.moveTo(fromX, fromY);
  ctx.lineTo(toX, toY);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(toX, toY);
  ctx.lineTo(toX - headSize * Math.cos(angle - 0.4), toY - headSize * Math.sin(angle - 0.4));
  ctx.lineTo(toX - headSize * Math.cos(angle + 0.4), toY - headSize * Math.sin(angle + 0.4));
  ctx.closePath();
  ctx.fill();
}

// ============ hit test (used by useCanvasInteraction) ============

export function hitTestFireSafety(item, pos) {
  const px = (item.size || 12) * PX_PER_INCH;
  const half = px * 0.75; // slightly larger than visual size for forgiving selection
  const dx = pos.x - item.x;
  const dy = pos.y - item.y;
  // Rotate point inverse
  const rad = -(item.rotation || 0) * Math.PI / 180;
  const rx = dx * Math.cos(rad) - dy * Math.sin(rad);
  const ry = dx * Math.sin(rad) + dy * Math.cos(rad);
  return rx >= -half && rx <= half && ry >= -half && ry <= half;
}
